import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import {
  CircleNotch, FloppyDisk, CheckCircle, Plus, Trash, Info,
  Target, ArrowsLeftRight, Plugs, ListChecks, GitBranch, Warning, Sparkle,
  Play, FlowArrow, Buildings, ClockCounterClockwise,
  ArrowDown, ArrowsClockwise, UploadSimple, Wrench, Database, ShieldCheck, Lightning, ClipboardText,
  ListBullets, MagnifyingGlass, Cube, Table, CaretLeft, CaretRight, Code, Globe, ArrowUp, XCircle,
  PlugsConnected, Funnel, ShoppingCart, MapPin, Megaphone,
} from "@phosphor-icons/react";

import WorkOrdersDashboard from "./MaximoWorkOrders";
import PageAdvice from "../components/PageAdvice";
import { IntegrationConnectionBar } from "../components/IntegrationConnectionBar";

const TABS = [
  { id: "overview", label: "Overview", icon: Info },
  { id: "workflow", label: "Workflow", icon: GitBranch },
  { id: "connection", label: "Connection", icon: Plugs },
  { id: "mapping", label: "Field Mapping", icon: ArrowsLeftRight },
  { id: "sync", label: "Sync (Dry Run)", icon: FlowArrow },
  { id: "workorders", label: "Work Orders", icon: ClipboardText },
  { id: "options", label: "API Options", icon: ListBullets },
  { id: "assets", label: "Assets", icon: Cube },
];

const OBJECTIVES = [
  "Automatically create & populate AroFlo tasks from Maximo work orders.",
  "Real-time or scheduled sync of status, logs, labour, materials and cost data.",
  "Improve data consistency and reduce manual administration.",
  "Enable condition reporting, statutory compliance and evidence-based records.",
];

const IN_SCOPE = [
  { title: "Work Order Creation & Task Sync", items: ["Detect new Maximo WOs routed to “GBELEC”", "Create AroFlo task with WO number, description, location, priority, target start/finish, customer ref"] },
  { title: "Automated Allocation & Scheduling", items: ["Allocate by priority, trade category, site rules", "Surface task on AroFlo Scheduling Calendar"] },
  { title: "Return Updates to Maximo", items: ["Update “Allocated To” to confirm import", "Status events: received, allocated, in-progress, completed, awaiting quote", "Push AroFlo notes back as Maximo Work Logs"] },
  { title: "Labour & Materials Capture", items: ["Sync actual labour (tech, times, hours, charge code)", "Sync materials, unit cost, qty, mark-ups", "Chargeable vs warranty vs PM/SM inclusions"] },
  { title: "Fixed-Price PM & SM Handling", items: ["Flag tasks as Fixed Price PM / SM", "Sync completion & condition without Maximo financial lines", "KPI & compliance reporting fields"] },
  { title: "Estimate / Quotation Workflow", items: ["Generate quotes for additional works", "Send to Maximo for review/approval", "Auto-generate WO updates on approval"] },
  { title: "Asset Mirroring & Compliance", items: ["Mirror Maximo asset registers (ID/barcode, type, model, location, compliance)", "Sync test-sheet results, condition scores, evidence photos", "Update condition, pass/fail, recommendations, risk ratings"] },
];

const DELIVERABLES = ["Functional Design", "Field Mapping Matrix", "API Config & Auth Setup", "End-to-End Testing", "Go-Live Deployment", "Support & Hypercare"];
const FUTURE = ["Real-time SLA dashboards", "Predictive maintenance exchange", "AI-driven condition assessments", "Asset lifecycle forecasting"];
const WORKFLOW_STAGES = ["Task received", "Task allocated", "In-progress", "Completed", "Awaiting quote / instruction"];

// System lanes for the end-to-end flowchart.
const LANE = {
  maximo: { name: "IBM Maximo", chip: "bg-[#0055FF]/10 text-[#0055FF]", ring: "border-[#0055FF]/40", bar: "bg-[#0055FF]", soft: "bg-[#0055FF]/[0.03]" },
  hub: { name: "Asset Hub (middleware)", chip: "bg-neutral-800 text-white", ring: "border-neutral-300", bar: "bg-[#0A0A0A]", soft: "bg-neutral-50" },
  aroflo: { name: "AroFlo", chip: "bg-[#059669]/10 text-[#059669]", ring: "border-[#059669]/40", bar: "bg-[#059669]", soft: "bg-[#059669]/[0.03]" },
};

// The full bi-directional lifecycle, grounded in the UON × GBE Maximo brief.
const FLOW_PHASES = [
  {
    n: 1, lane: "maximo", icon: Buildings, dir: null, write: false,
    title: "Work order raised & routed in Maximo",
    detail: [
      "UON raises a reactive or planned work order in IBM Maximo.",
      "The Lead field is set to GBELEC — the routing signal that the job belongs to GBE Electrical and is not yet in AroFlo.",
      "WO carries: WO number, Long/Short description, Campus, Target Start/Finish, Priority, Max W/O Cost, Work Log, Safety Plan.",
    ],
  },
  {
    n: 2, lane: "hub", icon: Database, dir: "Maximo → Hub", write: false,
    title: "Hub detects & ingests the work order",
    detail: [
      "Hub queries Maximo (MXAPIWODETAIL via OSLC REST) on a schedule, or receives a real-time/event trigger.",
      "Filters where Lead = GBELEC and the WO has not already been imported (e.g. \"Reactive WO due within 24 hours\").",
      "Validation: mandatory fields present, WO-number de-duplication, and the Max W/O Cost ceiling is recorded for later overspend guarding.",
    ],
  },
  {
    n: 3, lane: "hub", icon: ArrowsLeftRight, dir: "Transform", write: false,
    title: "Map Maximo fields → AroFlo task fields",
    mappings: [
      ["Location (Short) + Description (Short)", "Task Name"],
      ["Location (Long) + Description (Long)", "Description"],
      ["Work Order #", "Customer Order No"],
      ["Work Order Priority", "Priority"],
      ["Target Start / Target Finish", "Target Start / Finish"],
      ["Max Workorder Cost $", "Max W/O Cost"],
      ["Work Log + Safety Plan", "Task Notes"],
      ["(fixed) UON PO", "Task Type"],
      ["(fixed) University of Newcastle", "Client"],
      ["(fixed) JN400 Reactive Maintenance", "Owner"],
    ],
    detail: ["Campus / building short-codes resolve to the AroFlo Location (e.g. \"L\" → Auchmuty Library, \"L266\" → Building L)."],
  },
  {
    n: 4, lane: "aroflo", icon: UploadSimple, dir: "Hub → AroFlo", write: true,
    title: "Create the task in AroFlo",
    detail: [
      "Hub creates a new AroFlo task populated with all mapped fields.",
      "Task lands under Owner \"JN400 Reactive Maintenance\" for client \"University of Newcastle\".",
      "Currently a dry-run preview only — the actual write to AroFlo is gated until go-live.",
    ],
  },
  {
    n: 5, lane: "aroflo", icon: Wrench, dir: null, write: false,
    title: "Job is managed in AroFlo",
    detail: [
      "Auto/assisted allocation via Priority → Resource rules (P3/P4/P5 SLAs); the task appears on the AroFlo Scheduling Calendar.",
      "Technicians capture labour logs, materials, expenses and actual costs against the task.",
      "Quotes / RFQs / POs raised for additional works; fixed-price PM & SM handled without Maximo financial lines.",
      "Compliance captured: forms, OHS checklists, certifications, test-sheet results, condition scores and evidence photos.",
    ],
    stages: WORKFLOW_STAGES,
  },
  {
    n: 6, lane: "hub", icon: ArrowsClockwise, dir: "AroFlo → Hub", write: false,
    title: "AroFlo reports progress back to the Hub",
    detail: [
      "Hub reads AroFlo (scheduled sync or webhook) for every task linked to a Maximo WO.",
      "Pulls: status changes, work-log notes, actual labour (technician, times, hours, charge code), materials & expenses.",
      "Also pulls quote approvals and asset condition / compliance results for mirroring back.",
    ],
  },
  {
    n: 7, lane: "maximo", icon: UploadSimple, dir: "Hub → Maximo", write: true,
    title: "Convert & push back into Maximo",
    detail: [
      "Set Maximo \"Allocated To\" to confirm the WO was imported — the first round-trip acknowledgement.",
      "AroFlo notes → Maximo Work Logs; AroFlo task status → Maximo WO status.",
      "Actuals (labour, materials, expense) aggregated onto the Maximo WO; approved quotes drive WO updates.",
      "Condition / compliance results mirrored to the Maximo asset register. Write-back gated until Maximo API access + go-live.",
    ],
  },
];

// How a single job's state lines up across the three systems.
const STATUS_MAP = [
  ["New WO · Lead = GBELEC", "Detected / queued", "—"],
  ["Allocated (import confirmed)", "Imported", "Task received → allocated"],
  ["In progress", "Syncing", "In-progress"],
  ["WAPPR / on hold", "Reporting", "Awaiting quote / instruction"],
  ["Completed / Closed", "Reconciled", "Completed"],
];

const SAFEGUARDS = [
  { icon: Lightning, title: "Sync modes", body: "New WOs detected near real-time (event-driven); status, logs and actuals pushed back on a schedule." },
  { icon: ShieldCheck, title: "Validation", body: "Mandatory-field checks, WO-number de-duplication, and no overspend beyond Max W/O Cost without UON / BU4 approval." },
  { icon: ClockCounterClockwise, title: "Errors & reconciliation", body: "Every record logged with retries and admin alerts; periodic reconciliation report compares both systems for drift." },
];

function Saver({ onClick, saving, flash, label = "Save changes" }) {
  return (
    <button data-testid="maximo-save-btn" onClick={onClick} disabled={saving}
      className="inline-flex items-center gap-2 rounded-sm bg-[#059669] px-4 py-2 text-sm font-semibold text-white hover:bg-[#047857] disabled:opacity-60">
      {saving ? <CircleNotch size={15} className="animate-spin" /> : flash ? <CheckCircle size={15} weight="fill" /> : <FloppyDisk size={15} weight="bold" />}
      {flash ? "Saved" : label}
    </button>
  );
}

const inputCls = "w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0055FF]";

export default function MaximoIntegration() {
  const [config, setConfig] = useState({ connection: {}, routing: {} });
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const [connId, setConnId] = useState("");

  useEffect(() => {
    if (!connId) return;
    api.get("/maximo/config", { params: { connection_id: connId } }).then((r) => setConfig(r.data)).catch(() => {});
  }, [connId]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/maximo/config", patch, { params: { connection_id: connId } });
      setConfig(r.data);
      setFlash(true); setTimeout(() => setFlash(false), 2200);
    } finally { setSaving(false); }
  };

  const setConn = (k, v) => setConfig((p) => ({ ...p, connection: { ...p.connection, [k]: v } }));
  const setRouting = (k, v) => setConfig((p) => ({ ...p, routing: { ...p.routing, [k]: v } }));

  const conn = config.connection || {};
  const routing = config.routing || {};

  return (
    <Layout>
      <div data-testid="maximo-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Integration · Planning</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">MAXIMO Integration</h1>
        <p className="mt-2 max-w-3xl text-base text-neutral-600">Bi-directional AroFlo ↔ IBM Maximo work-order integration for University of Newcastle &amp; GBE Group.</p>

        <div className="mt-4 flex items-center gap-2 rounded-sm border border-[#D97706]/30 bg-[#D97706]/5 px-4 py-2.5" data-testid="maximo-status-banner">
          <Warning size={18} weight="fill" className="text-[#D97706]" />
          <span className="text-sm text-neutral-700">Planning &amp; configuration only — <span className="font-semibold">no live sync is active yet</span>. Nothing here connects to Maximo or AroFlo.</span>
        </div>

        {/* Tabs */}
        <div className="mt-6">
          <IntegrationConnectionBar provider="maximo" value={connId} onChange={setConnId} label="Maximo client connection" />
        </div>
        <div className="inline-flex flex-wrap gap-1 rounded-sm border border-neutral-200 bg-white p-1 shadow-sm">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`maximo-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 ${tab === t.id ? "bg-[#0A0A0A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
              <t.icon size={16} weight="bold" />{t.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div className="mt-6 space-y-6" data-testid="maximo-overview">
            <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
              <h2 className="flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Target size={18} weight="bold" className="text-[#0055FF]" /> Objectives</h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {OBJECTIVES.map((o) => <li key={o} className="flex gap-2 text-sm text-neutral-600"><CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-[#059669]" />{o}</li>)}
              </ul>
            </section>
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><ListChecks size={18} weight="bold" className="text-[#0055FF]" /> In-Scope Functional Areas</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {IN_SCOPE.map((s) => (
                  <div key={s.title} className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm">
                    <p className="font-semibold text-[#0A0A0A]">{s.title}</p>
                    <ul className="mt-2 space-y-1.5">
                      {s.items.map((it) => <li key={it} className="flex gap-2 text-sm text-neutral-600"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0055FF]" />{it}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
            <div className="grid gap-4 md:grid-cols-2">
              <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-bold text-[#0A0A0A]">Deliverables</h2>
                <ol className="mt-3 space-y-2">
                  {DELIVERABLES.map((d, i) => <li key={d} className="flex items-center gap-3 text-sm text-neutral-600"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0055FF]/10 text-xs font-bold text-[#0055FF]">{i + 1}</span>{d}</li>)}
                </ol>
              </section>
              <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
                <h2 className="flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Sparkle size={18} weight="bold" className="text-[#0055FF]" /> Future Enhancements</h2>
                <ul className="mt-3 space-y-2">
                  {FUTURE.map((f) => <li key={f} className="flex gap-2 text-sm text-neutral-600"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-300" />{f}</li>)}
                </ul>
                <p className="mt-4 rounded-sm bg-neutral-50 p-3 text-xs text-neutral-500"><span className="font-semibold">Out of scope:</span> Redesign of UON Maximo workflows (unless separately agreed).</p>
              </section>
            </div>
          </div>
        )}

        {/* WORK ORDERS */}
        {tab === "workorders" && <WorkOrdersDashboard />}
        {tab === "options" && <MaximoApiOptions />}
        {tab === "assets" && <MaximoAssets connId={connId} />}

        {/* FIELD MAPPING */}
        {tab === "mapping" && (
          <FieldMapping config={config} setConfig={setConfig} onSave={() => save({ field_mappings: config.field_mappings })} saving={saving} flash={flash} />
        )}

        {/* CONNECTION */}
        {tab === "connection" && (
          <div className="mt-6 max-w-2xl space-y-5 rounded-sm border border-neutral-200 bg-white p-6 shadow-sm" data-testid="maximo-connection">
            <div className="flex items-center gap-2">
              <span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Status: not connected</span>
              <span className="text-xs text-neutral-400">Saved for later — no requests are made.</span>
            </div>
            <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">AroFlo API version (target for this integration)</label>
              <select data-testid="maximo-aroflo-version" value={config.aroflo_api_version || "legacy"} onChange={(e) => setConfig((p) => ({ ...p, aroflo_api_version: e.target.value }))} className={inputCls}>
                <option value="legacy">Legacy API (Old) — more functionality, recommended first</option>
                <option value="v2">V2 API (New) — switch over once fully integrated</option>
              </select>
              <p className="mt-1 text-xs text-neutral-400">Reads pull live AroFlo data; writes stay disabled until go-live. Save via "Save connection".</p>
            </div>
            <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Maximo Base URL</label>
              <input data-testid="maximo-base-url" value={conn.base_url || ""} onChange={(e) => setConn("base_url", e.target.value)} placeholder="https://maximo.newcastle.edu.au/maximo/oslc" className={inputCls} /></div>
            <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">OSLC / OS Object</label>
              <input data-testid="maximo-os-object" value={conn.os_object || ""} onChange={(e) => setConn("os_object", e.target.value)} placeholder="MXAPIWODETAIL" className={inputCls} /></div>
            <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Authentication type</label>
              <select data-testid="maximo-auth-type" value={conn.auth_type || "apikey"} onChange={(e) => setConn("auth_type", e.target.value)} className={inputCls}>
                <option value="apikey">API Key (apikey header)</option>
                <option value="basic">Username &amp; Password (Basic)</option>
                <option value="oauth">OAuth 2.0</option>
              </select></div>
            {conn.auth_type === "apikey" && (
              <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">API Key</label>
                <input data-testid="maximo-api-key" value={conn.api_key || ""} onChange={(e) => setConn("api_key", e.target.value)} className={inputCls} spellCheck={false} /></div>
            )}
            {conn.auth_type !== "apikey" && (
              <div className="grid grid-cols-2 gap-4">
                <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Username</label>
                  <input data-testid="maximo-username" value={conn.username || ""} onChange={(e) => setConn("username", e.target.value)} className={inputCls} /></div>
                <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Password</label>
                  <input data-testid="maximo-password" type="password" value={conn.password || ""} onChange={(e) => setConn("password", e.target.value)} className={inputCls} /></div>
              </div>
            )}
            <div className="flex items-center justify-end border-t border-neutral-200 pt-4">
              <Saver onClick={() => save({ connection: config.connection, aroflo_api_version: config.aroflo_api_version })} saving={saving} flash={flash} label="Save connection" />
            </div>
          </div>
        )}

        {/* WORKFLOW */}
        {tab === "workflow" && (
          <div className="mt-6 space-y-6" data-testid="maximo-workflow">
            <WorkflowFlowchart />

            <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#0A0A0A]">Routing trigger</h2>
              <p className="mt-1 text-sm text-neutral-500">New Maximo work orders are imported when this field matches the value.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Trigger field</label>
                  <input data-testid="maximo-trigger-field" value={routing.trigger_field || ""} onChange={(e) => setRouting("trigger_field", e.target.value)} className={inputCls} /></div>
                <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Trigger value</label>
                  <input data-testid="maximo-trigger-value" value={routing.trigger_value || ""} onChange={(e) => setRouting("trigger_value", e.target.value)} className={inputCls} /></div>
                <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Sync mode</label>
                  <select data-testid="maximo-sync-mode" value={routing.sync_mode || "scheduled"} onChange={(e) => setRouting("sync_mode", e.target.value)} className={inputCls}>
                    <option value="scheduled">Scheduled polling</option>
                    <option value="realtime">Real-time triggers</option>
                  </select></div>
                <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Poll interval (min)</label>
                  <input data-testid="maximo-poll-interval" value={routing.poll_interval || ""} onChange={(e) => setRouting("poll_interval", e.target.value)} className={inputCls} /></div>
              </div>
            </section>

            <AllocationRules config={config} setConfig={setConfig} />

            <div className="flex items-center justify-end">
              <Saver onClick={() => save({ routing: config.routing, allocation_rules: config.allocation_rules })} saving={saving} flash={flash} label="Save workflow" />
            </div>
          </div>
        )}
        {/* SYNC (DRY RUN) */}
        {tab === "sync" && <SyncTab version={config.aroflo_api_version || "legacy"} />}

        <PageAdvice title="Important — planning & dry-run only" items={[
          "Nothing on this page writes to Aroflo or Maximo. Work orders, field mappings and the sync are illustrative until go-live access is granted.",
          "Work orders shown are mock records that mirror the GBELEC reactive-maintenance flow for audit demonstration.",
          "Live Maximo \u2192 Aroflo automation (Phase 2) is blocked pending Maximo API credentials and Aroflo write access.",
        ]} />
      </div>
    </Layout>
  );
}

function SyncTab({ version }) {
  const [mock, setMock] = useState(null);
  const [result, setResult] = useState(null);
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);

  const loadLog = () => api.get("/maximo/import-log").then((r) => setLog(r.data.entries || [])).catch(() => {});
  useEffect(() => {
    api.get("/maximo/mock-workorders").then((r) => setMock(r.data)).catch(() => setMock({ workorders: [] }));
    loadLog();
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/maximo/dry-run-import");
      setResult(r.data);
      loadLog();
    } finally { setRunning(false); }
  };

  return (
    <div className="mt-6 space-y-6" data-testid="maximo-sync">
      <div className="flex items-center gap-2 rounded-sm border border-[#0055FF]/30 bg-[#0055FF]/5 px-4 py-2.5">
        <Info size={18} weight="fill" className="text-[#0055FF]" />
        <span className="text-sm text-neutral-700">
          <span className="font-semibold">Dry run.</span> Maximo work orders are <span className="font-semibold">mocked</span>; AroFlo client data is <span className="font-semibold">live (read-only)</span>. Nothing is ever written to AroFlo here.
        </span>
      </div>

      {/* Mock Maximo work orders */}
      <section className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Buildings size={18} weight="bold" className="text-neutral-500" />
            <span className="font-semibold text-[#0A0A0A]">Inbound Maximo work orders</span>
            <span className="rounded-sm bg-[#D97706]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#D97706]">Mock</span>
          </div>
          <span className="text-xs text-neutral-400">Routing: Lead = GBELEC · AroFlo source: {mock?.aroflo_source || "—"}</span>
        </div>
        {!mock ? (
          <div className="flex h-32 items-center justify-center"><CircleNotch size={24} className="animate-spin text-[#0055FF]" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="maximo-mock-table">
              <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                {["WO #", "Description", "Location", "Priority", "Target start", "Customer (real AroFlo)"].map((h) => <th key={h} className="px-4 py-2.5 font-bold">{h}</th>)}
              </tr></thead>
              <tbody>
                {mock.workorders.map((w) => (
                  <tr key={w.wonum} className="border-b border-neutral-100 last:border-0" data-testid={`maximo-wo-${w.wonum}`}>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold text-[#0055FF]">{w.wonum}</td>
                    <td className="px-4 py-2.5">{w.description_short}</td>
                    <td className="px-4 py-2.5 text-neutral-600">{w.location}</td>
                    <td className="px-4 py-2.5"><span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-semibold">{w.priority}</span></td>
                    <td className="px-4 py-2.5 text-neutral-600">{w.target_start}</td>
                    <td className="px-4 py-2.5 text-neutral-700">{w.customer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-neutral-200 bg-neutral-50 px-5 py-3">
          <span className="text-sm text-neutral-500">{mock?.workorders?.length || 0} work orders ready to import (simulated)</span>
          <button data-testid="maximo-dryrun-btn" onClick={run} disabled={running}
            className="inline-flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0044CC] disabled:opacity-60">
            {running ? <CircleNotch size={15} className="animate-spin" /> : <Play size={15} weight="fill" />} Run dry-run import
          </button>
        </div>
      </section>

      {/* Preview of AroFlo tasks that WOULD be created */}
      {result && (
        <section className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm" data-testid="maximo-preview">
          <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
            <span className="font-semibold text-[#0A0A0A]">AroFlo tasks that would be created</span>
            <span className="rounded-sm bg-[#059669]/10 px-2 py-0.5 text-xs font-semibold text-[#059669]">{result.ready}/{result.total} ready · not written</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                {["Task name", "Priority", "Customer order", "Allocated to", "Matched client (real)"].map((h) => <th key={h} className="px-4 py-2.5 font-bold">{h}</th>)}
              </tr></thead>
              <tbody>
                {result.results.map((r, i) => (
                  <tr key={r.aroflo_task_preview?.customerorderno || `prev-${i}`} className="border-b border-neutral-100 last:border-0" data-testid={`maximo-preview-row-${i}`}>
                    <td className="max-w-sm truncate px-4 py-2.5" title={r.aroflo_task_preview.taskname}>{r.aroflo_task_preview.taskname}</td>
                    <td className="px-4 py-2.5"><span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-semibold">{r.aroflo_task_preview.priority}</span></td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.aroflo_task_preview.customerorderno}</td>
                    <td className="px-4 py-2.5 text-neutral-600">{r.aroflo_task_preview.allocated_to}</td>
                    <td className="px-4 py-2.5">{r.matched_client ? <span className="text-neutral-700">{r.matched_client.clientname}</span> : <span className="text-[#D97706]">no match</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Import log */}
      <section className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid="maximo-import-log">
        <div className="mb-3 flex items-center gap-2"><ClockCounterClockwise size={18} weight="bold" className="text-neutral-500" /><span className="font-semibold text-[#0A0A0A]">Import log</span></div>
        {log.length === 0 ? <p className="text-sm text-neutral-400">No imports run yet.</p> : (
          <div className="space-y-1.5">
            {log.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-neutral-100 py-1.5 text-sm last:border-0">
                <span className="font-mono text-xs text-neutral-400">{new Date(e.timestamp).toLocaleString()}</span>
                <span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-600">DRY RUN</span>
                <span className="text-neutral-600">{e.ready}/{e.total} ready</span>
                <span className="text-xs text-neutral-400">via {e.aroflo_api_version} · {e.by}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function WorkflowFlowchart() {
  return (
    <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm" data-testid="maximo-flowchart">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-[#0A0A0A]">
            <GitBranch size={18} weight="bold" className="text-[#0055FF]" /> End-to-end work-order lifecycle
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            How a Maximo work order flows through the Hub into AroFlo, is delivered, then reported back into Maximo — both directions.
          </p>
        </div>
        {/* System legend */}
        <div className="flex flex-wrap items-center gap-2" data-testid="maximo-flowchart-legend">
          {Object.entries(LANE).map(([k, l]) => (
            <span key={k} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${l.chip}`}>
              <span className={`h-2 w-2 rounded-full ${k === "hub" ? "bg-white" : l.bar}`} />{l.name}
            </span>
          ))}
        </div>
      </div>

      {/* Trigger callout */}
      <div className="mt-5 flex items-start gap-3 rounded-sm border border-[#0055FF]/30 bg-[#0055FF]/5 px-4 py-3" data-testid="maximo-flowchart-trigger">
        <Lightning size={18} weight="fill" className="mt-0.5 shrink-0 text-[#0055FF]" />
        <p className="text-sm text-neutral-700">
          <span className="font-semibold">Trigger:</span> a Maximo work order with <span className="rounded-sm bg-[#0055FF] px-1.5 py-0.5 font-mono text-xs font-bold text-white">Lead = GBELEC</span> that has not yet been imported. Everything below cascades from this single condition.
        </p>
      </div>

      {/* Vertical phased flow */}
      <ol className="mt-6">
        {FLOW_PHASES.map((p, idx) => {
          const lane = LANE[p.lane];
          const last = idx === FLOW_PHASES.length - 1;
          return (
            <li key={p.n} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`maximo-flow-step-${p.n}`}>
              {/* Rail */}
              <div className="flex flex-col items-center">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-black text-white ${lane.bar}`}>{p.n}</span>
                {!last && <span className="mt-1 w-px flex-1 bg-neutral-200" />}
              </div>

              {/* Card */}
              <div className={`flex-1 rounded-sm border ${lane.ring} ${lane.soft} p-4`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${lane.chip}`}>
                    <p.icon size={13} weight="bold" />{lane.name}
                  </span>
                  {p.dir && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-neutral-600 ring-1 ring-neutral-200">
                      <ArrowsLeftRight size={12} weight="bold" />{p.dir}
                    </span>
                  )}
                  {p.write && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#D97706]/10 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-[#D97706]">
                      <Warning size={12} weight="fill" />Write · gated until go-live
                    </span>
                  )}
                </div>

                <h3 className="mt-2 font-semibold text-[#0A0A0A]">{p.title}</h3>
                <ul className="mt-2 space-y-1.5">
                  {p.detail.map((d) => (
                    <li key={d} className="flex gap-2 text-sm text-neutral-600">
                      <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${lane.bar}`} />{d}
                    </li>
                  ))}
                </ul>

                {/* Field mapping grid */}
                {p.mappings && (
                  <div className="mt-3 grid gap-1.5 sm:grid-cols-2" data-testid="maximo-flow-mappings">
                    {p.mappings.map(([from, to]) => (
                      <div key={from} className="flex items-center gap-2 rounded-sm bg-white px-2.5 py-1.5 text-xs ring-1 ring-neutral-200">
                        <span className="truncate font-mono text-[#0055FF]" title={from}>{from}</span>
                        <ArrowsLeftRight size={12} weight="bold" className="shrink-0 text-neutral-400" />
                        <span className="truncate font-mono font-semibold text-[#059669]" title={to}>{to}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* AroFlo status stages */}
                {p.stages && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="maximo-flow-stages">
                    {p.stages.map((s, i) => (
                      <span key={s} className="flex items-center gap-1.5">
                        <span className="rounded-full bg-[#059669]/10 px-2.5 py-1 text-xs font-semibold text-[#059669]">{s}</span>
                        {i < p.stages.length - 1 && <ArrowDown size={12} className="rotate-[-90deg] text-neutral-300" />}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Status alignment table */}
      <div className="mt-6 overflow-hidden rounded-sm border border-neutral-200" data-testid="maximo-status-map">
        <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-2.5">
          <h3 className="text-sm font-bold text-[#0A0A0A]">Status alignment across systems</h3>
        </div>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="px-4 py-2 font-bold text-[#0055FF]">Maximo WO</th>
              <th className="px-4 py-2 font-bold text-neutral-700">Asset Hub</th>
              <th className="px-4 py-2 font-bold text-[#059669]">AroFlo task</th>
            </tr>
          </thead>
          <tbody>
            {STATUS_MAP.map((row) => (
              <tr key={row[0]} className="border-b border-neutral-100 last:border-0">
                {row.map((cell, j) => <td key={`${row[0]}-${j}`} className="px-4 py-2 text-neutral-600">{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Safeguards */}
      <div className="mt-6 grid gap-4 md:grid-cols-3" data-testid="maximo-safeguards">
        {SAFEGUARDS.map((s) => (
          <div key={s.title} className="rounded-sm border border-neutral-200 bg-neutral-50 p-4">
            <p className="flex items-center gap-2 text-sm font-bold text-[#0A0A0A]"><s.icon size={16} weight="bold" className="text-[#0055FF]" />{s.title}</p>
            <p className="mt-1.5 text-xs text-neutral-600">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const DIRECTION_OPTS = [
  { v: "inbound", label: "Maximo → AroFlo", cls: "bg-[#0055FF]/10 text-[#0055FF]" },
  { v: "return", label: "AroFlo → Maximo", cls: "bg-[#059669]/10 text-[#059669]" },
  { v: "trigger", label: "Trigger", cls: "bg-[#D97706]/10 text-[#D97706]" },
];

function FieldMapping({ config, setConfig, onSave, saving, flash }) {
  const rows = config.field_mappings || [];
  const setRow = (i, k, v) => setConfig((p) => { const m = [...p.field_mappings]; m[i] = { ...m[i], [k]: v }; return { ...p, field_mappings: m }; });
  const addRow = () => setConfig((p) => ({ ...p, field_mappings: [...p.field_mappings, { direction: "inbound", aroflo: "", maximo: "", notes: "" }] }));
  const delRow = (i) => setConfig((p) => ({ ...p, field_mappings: p.field_mappings.filter((_, j) => j !== i) }));
  return (
    <div className="mt-6" data-testid="maximo-mapping">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {DIRECTION_OPTS.map((d) => (
          <span key={d.v} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${d.cls}`}>
            <ArrowsLeftRight size={12} weight="bold" />{d.label}
          </span>
        ))}
      </div>
      <div className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
            <th className="px-4 py-3 font-bold">Direction</th><th className="px-4 py-3 font-bold">AroFlo element</th><th className="px-4 py-3 font-bold">Maximo element</th><th className="px-4 py-3 font-bold">Notes</th><th className="w-12" />
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const dir = DIRECTION_OPTS.find((d) => d.v === (r.direction || "inbound")) || DIRECTION_OPTS[0];
              return (
              <tr key={i} className="border-b border-neutral-100 last:border-0" data-testid={`maximo-map-row-${i}`}>
                <td className="px-3 py-2">
                  <select value={r.direction || "inbound"} onChange={(e) => setRow(i, "direction", e.target.value)} data-testid={`maximo-map-direction-${i}`}
                    className={`rounded-sm border-0 px-2 py-1.5 text-xs font-semibold outline-none ${dir.cls}`}>
                    {DIRECTION_OPTS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2"><input value={r.aroflo} onChange={(e) => setRow(i, "aroflo", e.target.value)} className={inputCls} data-testid={`maximo-map-aroflo-${i}`} /></td>
                <td className="px-3 py-2"><input value={r.maximo} onChange={(e) => setRow(i, "maximo", e.target.value)} className={inputCls} data-testid={`maximo-map-maximo-${i}`} /></td>
                <td className="px-3 py-2"><input value={r.notes} onChange={(e) => setRow(i, "notes", e.target.value)} className={inputCls} placeholder="optional" /></td>
                <td className="px-2"><button data-testid={`maximo-map-del-${i}`} onClick={() => delRow(i)} className="text-neutral-400 hover:text-[#DC2626]"><Trash size={16} /></button></td>
              </tr>
              );
            })}
          </tbody>
        </table>
        <div className="border-t border-neutral-200 p-3">
          <button data-testid="maximo-map-add" onClick={addRow} className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"><Plus size={15} weight="bold" /> Add mapping</button>
        </div>
      </div>
      <div className="mt-4 flex justify-end"><Saver onClick={onSave} saving={saving} flash={flash} label="Save mappings" /></div>
    </div>
  );
}

function AllocationRules({ config, setConfig }) {
  const rules = config.allocation_rules || [];
  const setRule = (i, k, v) => setConfig((p) => { const a = [...p.allocation_rules]; a[i] = { ...a[i], [k]: v }; return { ...p, allocation_rules: a }; });
  const addRule = () => setConfig((p) => ({ ...p, allocation_rules: [...p.allocation_rules, { priority: "", resource: "" }] }));
  const delRule = (i) => setConfig((p) => ({ ...p, allocation_rules: p.allocation_rules.filter((_, j) => j !== i) }));
  return (
    <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm" data-testid="maximo-allocation">
      <h2 className="text-lg font-bold text-[#0A0A0A]">Priority → resource allocation</h2>
      <p className="mt-1 text-sm text-neutral-500">Map Maximo priorities (P3/P4/P5) to the AroFlo resource or queue.</p>
      <div className="mt-4 space-y-2">
        {rules.map((r, i) => (
          <div key={i} className="flex items-center gap-3" data-testid={`maximo-alloc-row-${i}`}>
            <input value={r.priority} onChange={(e) => setRule(i, "priority", e.target.value)} placeholder="P3" className={`${inputCls} w-24`} data-testid={`maximo-alloc-priority-${i}`} />
            <span className="text-neutral-300">→</span>
            <input value={r.resource} onChange={(e) => setRule(i, "resource", e.target.value)} placeholder="Resource / queue name" className={inputCls} data-testid={`maximo-alloc-resource-${i}`} />
            <button data-testid={`maximo-alloc-del-${i}`} onClick={() => delRule(i)} className="text-neutral-400 hover:text-[#DC2626]"><Trash size={16} /></button>
          </div>
        ))}
      </div>
      <button data-testid="maximo-alloc-add" onClick={addRule} className="mt-3 inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"><Plus size={15} weight="bold" /> Add rule</button>
    </section>
  );
}


// ---------------------------------------------------------------------------
// API Options — catalogue of Maximo OSLC REST APIs (object structures)
// ---------------------------------------------------------------------------
const MX_FACTS = [
  { icon: Globe, title: "OSLC REST / JSON", body: "Maximo's Integration Framework exposes object structures over OSLC REST. Add lean=1 for flat, readable JSON." },
  { icon: Database, title: "Object Structures (MXAPI*)", body: "Each business object is published as an MXAPI* object structure at /maximo/oslc/os/{name}." },
  { icon: ShieldCheck, title: "API key or Basic auth", body: "Authenticate with an apikey header (recommended) or a maxauth / Basic username:password header." },
  { icon: Funnel, title: "OSLC query language", body: "Filter, select, sort and page server-side with oslc.where, oslc.select, oslc.orderBy & oslc.pageSize." },
];

const MX_RESOURCES = [
  { icon: Cube, name: "Assets", os: "MXAPIASSET", desc: "Asset register — assetnum, description, status, location, siteid, serialnum, manufacturer, parent.", use: "Mirror the Maximo asset register into Asset Hub; align with AroFlo assets." },
  { icon: ClipboardText, name: "Work Orders (detail)", os: "MXAPIWODETAIL", desc: "Full work order detail — wonum, description, status, worktype, location, assetnum, target dates, labor, materials.", use: "Detect & ingest WOs routed to GBELEC; sync status, logs and actuals." },
  { icon: ClipboardText, name: "Work Orders (core)", os: "MXAPIWO", desc: "Core work order object (lighter than WODETAIL).", use: "Lightweight WO lookups & status." },
  { icon: MapPin, name: "Locations", os: "MXAPILOCATIONS", desc: "Operating locations / location hierarchy — location, description, type, siteid.", use: "Resolve campus / building codes for task locations." },
  { icon: Database, name: "Items", os: "MXAPIITEM", desc: "Item master / catalogue — itemnum, description, commodity group.", use: "Materials reconciliation against jobs." },
  { icon: Database, name: "Inventory", os: "MXAPIINVENTORY", desc: "Inventory balances by storeroom.", use: "Stock availability (future)." },
  { icon: ShoppingCart, name: "Purchase Orders", os: "MXAPIPO", desc: "Purchase orders & lines.", use: "Cost reconciliation (future)." },
  { icon: ShoppingCart, name: "Purchase Requisitions", os: "MXAPIPR", desc: "Purchase requisitions.", use: "Procurement workflow (future)." },
  { icon: Wrench, name: "Preventive Maintenance", os: "MXAPIPM", desc: "PM schedules & frequencies.", use: "Planned / fixed-price PM handling." },
  { icon: ListChecks, name: "Job Plans", os: "MXAPIJOBPLAN", desc: "Job plans, tasks, labor & material plans.", use: "Pre-fill task scopes." },
  { icon: Wrench, name: "Labor", os: "MXAPILABOR", desc: "Labour records & craft rates.", use: "Map technicians / charge codes." },
  { icon: Buildings, name: "Companies", os: "MXAPICOMPANY", desc: "Vendors / companies.", use: "Supplier linkage (future)." },
  { icon: Megaphone, name: "Service Requests", os: "MXAPISR", desc: "Service requests / tickets.", use: "Reactive request intake (future)." },
];

const MX_OPERATIONS = [
  { verb: "GET", dir: "pull", name: "List a collection", ex: "GET /os/mxapiasset?lean=1", note: "All records in the object structure (paged)." },
  { verb: "GET", dir: "pull", name: "Read by id", ex: "GET /os/mxapiasset/{id}?lean=1", note: "Single record by its unique id / href." },
  { verb: "GET", dir: "pull", name: "Filter (where)", ex: 'GET /os/mxapiasset?oslc.where=status="OPERATING"', note: "Server-side filtering with the OSLC where clause." },
  { verb: "GET", dir: "pull", name: "Select / shape fields", ex: "GET /os/mxapiwodetail?oslc.select=wonum,description,status", note: "Combine with oslc.orderBy, oslc.pageSize, pageno." },
  { verb: "POST", dir: "push", name: "Create a record", ex: "POST /os/mxapiasset  { … }", note: "Create e.g. an asset or work order." },
  { verb: "POST", dir: "push", name: "Update a record", ex: "POST /os/mxapiasset/{id}  (x-method-override: PATCH)", note: "Partial update via PATCH override." },
  { verb: "POST", dir: "push", name: "Bulk operation", ex: "POST /os/mxapiasset/bulk  [ … ]", note: "Create / update many records in one call." },
  { verb: "DELETE", dir: "push", name: "Delete a record", ex: "DELETE /os/mxapiasset/{id}", note: "Remove a record by id." },
];

const MX_QUERY = ["oslc.select", "oslc.where", "oslc.orderBy", "oslc.pageSize", "pageno", "lean", "_dropnulls", "collectioncount"];
const MX_DIR = {
  pull: { label: "Read", cls: "bg-[#0055FF]/10 text-[#0055FF]", icon: ArrowDown },
  push: { label: "Write", cls: "bg-[#D97706]/10 text-[#D97706]", icon: ArrowUp },
};
const MX_VERB = { GET: "bg-[#059669]/10 text-[#059669]", POST: "bg-[#0055FF]/10 text-[#0055FF]", DELETE: "bg-[#DC2626]/10 text-[#DC2626]" };

function MaximoApiOptions() {
  const [base, setBase] = useState("{maximo-base}");
  useEffect(() => { api.get("/maximo/config").then((r) => { const b = r.data?.connection?.base_url; if (b) setBase(b.replace(/\/$/, "")); }).catch(() => {}); }, []);
  return (
    <div className="mt-6 space-y-6" data-testid="maximo-options">
      <div className="grid gap-4 sm:grid-cols-2">
        {MX_FACTS.map((f) => (
          <div key={f.title} className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm">
            <p className="flex items-center gap-2 font-semibold text-[#0A0A0A]"><f.icon size={18} weight="bold" className="text-[#0055FF]" />{f.title}</p>
            <p className="mt-1.5 text-sm text-neutral-600">{f.body}</p>
          </div>
        ))}
      </div>

      <section>
        <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Database size={18} weight="bold" className="text-[#0055FF]" /> Object structures you can connect to</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {MX_RESOURCES.map((r) => (
            <div key={r.os} className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid={`maximo-resource-${r.os}`}>
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 font-semibold text-[#0A0A0A]"><r.icon size={18} weight="bold" className="text-[#0055FF]" />{r.name}</p>
                <code className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">/os/{r.os.toLowerCase()}</code>
              </div>
              <p className="mt-2 text-sm text-neutral-600">{r.desc}</p>
              <p className="mt-2 flex gap-1.5 text-xs text-neutral-500"><ArrowsLeftRight size={13} className="mt-0.5 shrink-0 text-[#059669]" /><span><span className="font-semibold">Asset Hub use:</span> {r.use}</span></p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Funnel size={18} weight="bold" className="text-[#0055FF]" /> Operations available per object structure</h2>
        <div className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="px-4 py-3 font-bold">Method</th><th className="px-4 py-3 font-bold">Type</th><th className="px-4 py-3 font-bold">Operation</th><th className="px-4 py-3 font-bold">Example</th><th className="px-4 py-3 font-bold">Notes</th>
            </tr></thead>
            <tbody>
              {MX_OPERATIONS.map((op) => {
                const d = MX_DIR[op.dir];
                return (
                  <tr key={op.name + op.ex} className="border-b border-neutral-100 last:border-0">
                    <td className="px-4 py-3"><span className={`rounded-sm px-2 py-0.5 text-xs font-bold ${MX_VERB[op.verb]}`}>{op.verb}</span></td>
                    <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${d.cls}`}><d.icon size={11} weight="bold" />{d.label}</span></td>
                    <td className="px-4 py-3 font-medium text-[#0A0A0A]">{op.name}</td>
                    <td className="px-4 py-3"><code className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700">{op.ex}</code></td>
                    <td className="px-4 py-3 text-neutral-500">{op.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-neutral-400">Write operations (POST / PATCH / DELETE) stay disabled until go-live scope is agreed. Asset Hub starts with read-only pulls.</p>
      </section>

      <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><MagnifyingGlass size={18} weight="bold" className="text-[#0055FF]" /> OSLC query parameters</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {MX_QUERY.map((q) => <code key={q} className="rounded-sm bg-[#0055FF]/10 px-2.5 py-1 text-xs font-semibold text-[#0055FF]">{q}</code>)}
        </div>
      </section>

      <section>
        <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Code size={18} weight="bold" className="text-[#0055FF]" /> Example requests</h2>
        <div className="space-y-3">
          {[
            { label: "List assets (flat JSON)", code: `curl "${base}/os/mxapiasset?lean=1&oslc.pageSize=25" -H "apikey: <API_KEY>"` },
            { label: "Operating assets only, selected fields", code: `curl -G "${base}/os/mxapiasset" --data-urlencode "lean=1" --data-urlencode 'oslc.where=status="OPERATING"' --data-urlencode "oslc.select=assetnum,description,location,status" -H "apikey: <API_KEY>"` },
            { label: "Work orders routed to GBELEC", code: `curl -G "${base}/os/mxapiwodetail" --data-urlencode "lean=1" --data-urlencode 'oslc.where=lead="GBELEC"' -H "apikey: <API_KEY>"` },
          ].map((ex) => (
            <div key={ex.label} className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
              <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-2 text-xs font-semibold text-neutral-600">{ex.label}</div>
              <pre className="overflow-x-auto px-4 py-3 text-xs text-neutral-800"><code>{ex.code}</code></pre>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assets — live read-only pull of assets accessible via Maximo
// ---------------------------------------------------------------------------
function MaximoAssets({ connId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debounced, connId]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/maximo/assets", { params: { page, limit: 25, search: debounced, connection_id: connId } });
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail)); setData(null);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, debounced, connId]);

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/maximo/test-connection", null, { params: { connection_id: connId } });
      setTestResult(data);
      if (data.connected) { toast.success(data.message); load(); } else { toast.error(data.message); }
    } catch (e) {
      setTestResult({ connected: false, message: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setTesting(false); }
  };

  const cols = data?.columns || [];
  const total = data?.count;
  const pages = total != null ? Math.max(1, Math.ceil(total / 25)) : null;

  return (
    <div className="mt-6 space-y-4" data-testid="maximo-assets">
      <div className="flex items-center gap-2 rounded-sm border border-[#0055FF]/30 bg-[#0055FF]/5 px-4 py-2.5">
        <Info size={18} weight="fill" className="text-[#0055FF]" />
        <span className="text-sm text-neutral-700"><span className="font-semibold">Read-only.</span> Live assets pulled from Maximo (MXAPIASSET) over OSLC REST. Nothing is written back to Maximo.</span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button data-testid="maximo-test-btn" onClick={test} disabled={testing}
          className="inline-flex items-center gap-2 rounded-sm border border-[#0055FF] px-3 py-2 text-sm font-semibold text-[#0055FF] hover:bg-[#0055FF]/5 disabled:opacity-60">
          {testing ? <CircleNotch size={15} className="animate-spin" /> : <PlugsConnected size={15} weight="bold" />} Test connection
        </button>
        <div className="flex items-center gap-2">
          <div className="relative w-full max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input data-testid="maximo-assets-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search asset number or description…"
              className="w-full rounded-sm border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#0055FF]" />
          </div>
          <button data-testid="maximo-assets-refresh" onClick={load} disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:border-[#0055FF] disabled:opacity-50">
            <ArrowsClockwise size={15} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {testResult && (
        <div className={`flex items-center gap-2 rounded-sm border px-4 py-2.5 text-sm ${testResult.connected ? "border-[#059669]/30 bg-[#059669]/5 text-[#059669]" : "border-[#DC2626]/30 bg-[#DC2626]/5 text-[#DC2626]"}`} data-testid="maximo-test-result">
          {testResult.connected ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />} {testResult.message}
        </div>
      )}

      <div className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : error ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-[#D97706]" data-testid="maximo-assets-error"><Warning size={28} weight="fill" /> {error}</div>
        ) : !data || data.items.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-400" data-testid="maximo-assets-empty"><Cube size={30} /> No assets returned.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="maximo-assets-table">
              <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                {cols.map((c) => <th key={c} className="whitespace-nowrap px-4 py-2.5 text-left font-bold">{c}</th>)}
              </tr></thead>
              <tbody>
                {data.items.map((row, i) => (
                  <tr key={i} className="border-b border-neutral-100 last:border-0 even:bg-neutral-50/50" data-testid={`maximo-asset-row-${i}`}>
                    {cols.map((c) => <td key={c} className="whitespace-nowrap px-4 py-2.5 text-neutral-700">{row[c] == null || row[c] === "" ? "—" : String(row[c])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data && data.items.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-500" data-testid="maximo-assets-count">
            {total != null ? `${total.toLocaleString()} assets` : `${data.returned} shown`}{pages ? ` · page ${page} of ${pages}` : ""}
          </p>
          <div className="flex gap-2">
            <button data-testid="maximo-assets-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40"><CaretLeft size={14} weight="bold" /> Prev</button>
            <button data-testid="maximo-assets-next" disabled={pages != null ? page >= pages : data.returned < 25} onClick={() => setPage((p) => p + 1)} className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40">Next <CaretRight size={14} weight="bold" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
